'use client'

import { useMemo, useState, useDeferredValue } from 'react'
import Link from 'next/link'
import { t, cityPath } from '../lib/i18n'

/**
 * The city index.
 *
 * Replaces CitiesListClient (723 lines), which was the last legacy page on the site and looked it:
 * two pale BLUE panels (#f0f9ff, #e7f3ff, rgba(0,112,243,…) — leftovers from the Vercel-blue
 * palette this design replaced), a ⭐ in a filter pill, a 🔍 in the search field, and three cards
 * shouting "153 / 102 / 58" under the heading "#1 DESTACADA". On a page whose palette is grey paper
 * and one signal red, the blue was not a style choice, it was a fossil.
 *
 * The filters went too. "Top ciudades" and "≥5 parques" are not filters, they are sorts wearing a
 * filter's clothes — and "Con ubicación" offered to hide the cities you can actually map, which is
 * the whole product. One search box and one sort. If you want the biggest, sort by size.
 *
 * The row is the same `.city` row as the home page: rank, name, province, a density bar, a count.
 * The bar is what a card grid cannot do — it makes the skew legible. Madrid has 153. The median
 * town has one.
 */

const normalise = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // so "malaga" finds "Málaga"

export default function CityIndex({ cities, locale = 'es' }) {
  const T = t(locale)
  const [q, setQ] = useState('')
  const [sort, setSort] = useState('parks') // 'parks' | 'az'
  const deferredQ = useDeferredValue(q)

  const rows = useMemo(() => {
    const needle = normalise(deferredQ.trim())
    let out = cities
    if (needle) {
      out = cities.filter(
        (c) =>
          normalise(c.city_name).includes(needle) ||
          normalise(c.province).includes(needle) ||
          normalise(c.region).includes(needle),
      )
    }
    const sorted = [...out]
    if (sort === 'az') {
      sorted.sort((a, b) => a.city_name.localeCompare(b.city_name, 'es'))
    } else {
      sorted.sort((a, b) => (b.total_parks || 0) - (a.total_parks || 0))
    }
    return sorted
  }, [cities, deferredQ, sort])

  // The bar is proportional to the largest city IN THE WHOLE SET, not to the largest currently
  // visible. Rescaling per filter would make a 3-park town draw a full-width bar the moment you
  // searched for it, which is a lie the chart tells only when you are not looking.
  const maxParks = useMemo(
    () => cities.reduce((m, c) => Math.max(m, c.total_parks || 0), 1),
    [cities],
  )

  return (
    <section className="section">
      <div className="controls">
        <label className="field">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={T.searchCity}
            aria-label={T.searchCity}
          />
        </label>

        <button
          type="button"
          className="btn"
          aria-pressed={sort === 'parks'}
          onClick={() => setSort('parks')}
        >
          {T.mostParks}
        </button>
        <button
          type="button"
          className="btn"
          aria-pressed={sort === 'az'}
          onClick={() => setSort('az')}
        >
          {T.az}
        </button>
      </div>

      <p className="result-note">
        {rows.length === cities.length
          ? T.nMunicipalities(cities.length.toLocaleString(locale === 'en' ? 'en-GB' : 'es-ES'))
          : T.ofN(rows.length.toLocaleString(locale === 'en' ? 'en-GB' : 'es-ES'), T.nMunicipalities(cities.length.toLocaleString(locale === 'en' ? 'en-GB' : 'es-ES')))}
      </p>

      {rows.length === 0 ? (
        <p className="empty">
          {T.noMatch(q)}
        </p>
      ) : (
        <div className="cities">
          {rows.map((city, i) => (
            <Link key={city.city_slug} className="city" href={cityPath(locale, city.city_slug)}>
              <span className="city-rank">
                {sort === 'parks' ? String(i + 1).padStart(2, '0') : ''}
              </span>
              <span>
                <span className="city-name">{city.city_name}</span>
                {city.province && city.province !== city.city_name && (
                  <span className="city-place">{city.province}</span>
                )}
              </span>
              <span className="city-bar" aria-hidden="true">
                <i style={{ width: `${Math.max(3, ((city.total_parks || 0) / maxParks) * 100)}%` }} />
              </span>
              <span className="city-count">{city.total_parks}</span>
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}
